import { useState, useEffect } from 'react';
import { createTask, updateTask, uploadImage, uploadFile, getImageUrl } from '@/utils/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Save, X } from 'lucide-react';

import { TaskBasicTab } from './task-detail/TaskBasicTab';
import { TaskMetalTab } from './task-detail/TaskMetalTab';
import { TaskDiamondsTab } from './task-detail/TaskDiamondsTab';
import { TaskFinishingTab } from './task-detail/TaskFinishingTab';
import { TaskFilesTab } from './task-detail/TaskFilesTab';
import { TaskWorkflowTab } from './task-detail/TaskWorkflowTab';

// Field names mirror backend `DiamondDetail` / `OtherStone` (the canonical names used
// by the CAD sheet sync). `stone_category` is the stone identity (Natural/Lab/CVD/...),
// `category` is the position (Center/Accent/Side), `ownership` is KS Goods/Party Goods.
const emptyDiamond = {
  stone_category: 'Natural',
  ownership: 'KS Goods',
  stone_shape: 'Round',
  category: 'Accent Stone',
  stone_quality: '',
  setting_type: '',
  size_mm: '',
  weight_per_stone: 0,
  quantity: 1,
};
const emptyStone = { stone_name: '', shape: 'Oval', size_mm: '', pieces: 1, weight_ct: 0, setting_type: '' };

const TaskDetailForm = ({ task, onUpdate, onCancel }) => {
  const { user } = useAuth();
  const isCustomer = user?.role === 'customer';
  const isEditMode = !!task;
  const canEdit = true; // Allow all authorized roles to edit details

  const [formData, setFormData] = useState({
    party_cad_no: '', style_no: '', client_name: '', product_name: '', pd_code: '',
    piece_type: 'ring', category: '', collection_name: '', size: '',
    metal_type: '', metal_colour: '', metal_karat: '',
    gross_weight: '', net_weight: '', target_weight: '',
    diamond_type: '', total_diamond_pieces: '', total_diamond_weight_ct: '', diamond_quality_code: '',
    diamonds: [], other_stones: [],
    cad_file_name: '', cad_file_format: '', cad_server_path: '', render_file_name: '',
    product_image: '', cad_file: '', additional_images: [],
    stage: '', priority: 'normal', start_date: '', target_date: '',
    quotation_type: '', quotation_file_name: '',
    estimated_metal_cost: '', estimated_diamond_cost: '', estimated_total_cost: '',
    notes: '', special_instructions: '', manufacturing_constraints: '', client_feedback: '',
    // Engraving / Stamping / Finishing — exposed via TaskFinishingTab.
    engraving: '', engraving_image: '',
    stamping_instructions: '', stamping_images: [],
    finishing: '', rhodium_plating: '',
  });

  const [imageUrl, setImageUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (task) {
      setFormData({
        party_cad_no: task.party_cad_no || '', style_no: task.style_no || '',
        client_name: task.client_name || '', product_name: task.product_name || '',
        pd_code: task.pd_code || '', piece_type: task.piece_type || 'ring',
        category: task.category || '', collection_name: task.collection_name || '',
        size: task.size || '', metal_type: task.metal_type || '',
        metal_colour: task.metal_colour || '', metal_karat: task.metal_karat || '',
        gross_weight: task.gross_weight || '', net_weight: task.net_weight || '',
        target_weight: task.target_weight || '',
        diamond_type: task.diamond_type || '',
        total_diamond_pieces: task.total_diamond_pieces || '',
        total_diamond_weight_ct: task.total_diamond_weight_ct || '',
        diamond_quality_code: task.diamond_quality_code || '',
        diamonds: task.diamonds || [], other_stones: task.other_stones || [],
        cad_file_name: task.cad_file_name || '', cad_file_format: task.cad_file_format || '',
        cad_server_path: task.cad_server_path || '', render_file_name: task.render_file_name || '',
        product_image: task.product_image || '', cad_file: task.cad_file || '', additional_images: task.additional_images || [],
        stage: task.stage || '', priority: task.priority || 'normal',
        start_date: task.start_date ? new Date(task.start_date).toISOString().split('T')[0] : '',
        target_date: task.target_date ? new Date(task.target_date).toISOString().split('T')[0] : '',
        quotation_type: task.quotation_type || '', quotation_file_name: task.quotation_file_name || '',
        estimated_metal_cost: task.estimated_metal_cost || '',
        estimated_diamond_cost: task.estimated_diamond_cost || '',
        estimated_total_cost: task.estimated_total_cost || '',
        notes: task.notes || '', special_instructions: task.special_instructions || '',
        manufacturing_constraints: task.manufacturing_constraints || '',
        client_feedback: task.client_feedback || '',
        engraving: task.engraving || '',
        engraving_image: task.engraving_image || '',
        stamping_instructions: task.stamping_instructions || '',
        stamping_images: task.stamping_images || [],
        // Read finishing first; fall back to legacy `texture` for un-migrated rows.
        finishing: task.finishing || task.texture || '',
        rhodium_plating: task.rhodium_plating || '',
      });
      setImageUrl(task.product_image || '');
    }
  }, [task]);

  const handleInputChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleImageUpload = async (e, field = 'product_image') => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const isImage = file.type.startsWith('image/');
      const response = isImage ? await uploadImage(file) : await uploadFile(file);
      const path = response.data.image || response.data.url || response.data.path;
      setFormData(prev => ({ ...prev, [field]: path }));
      if (field === 'product_image') {
        setImageUrl(path);
      }
      toast.success('File uploaded');
    } catch { toast.error('Failed to upload file'); }
    finally { setUploading(false); }
  };

  const handleImageUrl = () => {
    if (imageUrl) { setFormData(prev => ({ ...prev, product_image: imageUrl })); toast.success('Image URL added'); }
  };

  const handleEngravingImageUpload = async (file) => {
    try {
      const response = await uploadImage(file);
      const url = response.data.image;
      setFormData(prev => ({ ...prev, engraving_image: url }));
      toast.success('Engraving image uploaded');
    } catch { toast.error('Failed to upload engraving image'); }
  };

  const handleStampingImageUpload = async (file) => {
    try {
      const response = await uploadImage(file);
      const url = response.data.image;
      setFormData(prev => ({ ...prev, stamping_images: [...(prev.stamping_images || []), url] }));
      toast.success('Stamping image uploaded');
    } catch { toast.error('Failed to upload stamping image'); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const submitData = {
        ...formData,
        start_date: formData.start_date ? new Date(formData.start_date).toISOString() : null,
        target_date: formData.target_date ? new Date(formData.target_date).toISOString() : null,
        gross_weight: formData.gross_weight ? parseFloat(formData.gross_weight) : null,
        net_weight: formData.net_weight ? parseFloat(formData.net_weight) : null,
        target_weight: formData.target_weight ? parseFloat(formData.target_weight) : null,
        total_diamond_pieces: formData.total_diamond_pieces ? parseInt(formData.total_diamond_pieces) : null,
        total_diamond_weight_ct: formData.total_diamond_weight_ct ? parseFloat(formData.total_diamond_weight_ct) : null,
        estimated_metal_cost: formData.estimated_metal_cost ? parseFloat(formData.estimated_metal_cost) : null,
        estimated_diamond_cost: formData.estimated_diamond_cost ? parseFloat(formData.estimated_diamond_cost) : null,
        estimated_total_cost: formData.estimated_total_cost ? parseFloat(formData.estimated_total_cost) : null,
      };
      if (isEditMode) { await updateTask(task.id, submitData); toast.success('Task updated'); }
      else { await createTask(submitData); toast.success('Task created'); }
      onUpdate();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save task'); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={handleSubmit} className="h-full flex flex-col">
      <div className="p-6 border-b border-slate-200 flex items-center justify-between bg-slate-50">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">{isEditMode ? `CAD: ${task.internal_cad_no}` : 'New CAD File'}</h2>
          {isEditMode && <p className="text-sm text-slate-600 mt-1">Party No: {task.party_cad_no} | Created: {new Date(task.created_at).toLocaleDateString()}</p>}
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={onCancel} size="sm"><X size={16} className="mr-2" />Cancel</Button>
          {canEdit && <Button type="submit" disabled={saving} size="sm" className="bg-slate-900 hover:bg-slate-800"><Save size={16} className="mr-2" />{saving ? 'Saving...' : 'Save'}</Button>}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        <Tabs defaultValue="basic" className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="basic">Basic Info</TabsTrigger>
            <TabsTrigger value="metal">Metal</TabsTrigger>
            <TabsTrigger value="diamonds">Diamonds</TabsTrigger>
            <TabsTrigger value="finishing">Finishing</TabsTrigger>
            <TabsTrigger value="files">Files</TabsTrigger>
            {!isCustomer && <TabsTrigger value="workflow">Workflow</TabsTrigger>}
          </TabsList>
          <TabsContent value="basic"><TaskBasicTab formData={formData} onChange={handleInputChange} canEdit={canEdit} /></TabsContent>
          <TabsContent value="metal"><TaskMetalTab formData={formData} onChange={handleInputChange} canEdit={canEdit} /></TabsContent>
          <TabsContent value="diamonds"><TaskDiamondsTab formData={formData} onChange={handleInputChange} canEdit={canEdit} emptyDiamond={emptyDiamond} emptyStone={emptyStone} /></TabsContent>
          <TabsContent value="finishing"><TaskFinishingTab formData={formData} onChange={handleInputChange} canEdit={canEdit} onUploadEngravingImage={handleEngravingImageUpload} onUploadStampingImages={handleStampingImageUpload} /></TabsContent>
          <TabsContent value="files"><TaskFilesTab formData={formData} onChange={handleInputChange} canEdit={canEdit} onImageUpload={handleImageUpload} uploading={uploading} imageUrl={imageUrl} setImageUrl={setImageUrl} onImageUrl={handleImageUrl} /></TabsContent>
          {!isCustomer && <TabsContent value="workflow"><TaskWorkflowTab formData={formData} onChange={handleInputChange} canEdit={canEdit} /></TabsContent>}
        </Tabs>
      </div>
    </form>
  );
};

export default TaskDetailForm;
